import { useState } from "react";
import { createContainer } from "unstated-next";
import IResult from "../models/result/IResult";
import * as voicifyApi from '../api';
import MedicalIntegrationConfigModel from "../models/organizations/MedicalIntegrationConfigModel";

function useMedicalIntegrationConfigContainer() {
    const [medicalConfigs, setMedicalConfigs] = useState<MedicalIntegrationConfigModel[]>([]);
    const [selectedConfig, setSelectedConfig] = useState<MedicalIntegrationConfigModel | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const loadConfigsByOrganizationId = async (organizationId: string): Promise<IResult<MedicalIntegrationConfigModel[]>> => {
        setIsLoading(true);
        setErrors([]);
        try {
            const result = await voicifyApi.getMedicalIntegrationConfigsByOrganizationId(organizationId);
            if (result.resultType === "Ok") {
                setMedicalConfigs(result.data || []);
            } else {
                setErrors(result.errors || ['Failed to load configurations']);
            }
            return result;
        } catch (e) {
            const errorMessage = e?.toString() || 'Unknown error';
            setErrors([errorMessage]);
            return {
                resultType: "InternalError",
                errors: [errorMessage],
                data: null
            } as IResult<MedicalIntegrationConfigModel[]>;
        } finally {
            setIsLoading(false);
        }
    };

    const createConfig = async (model: MedicalIntegrationConfigModel): Promise<IResult<MedicalIntegrationConfigModel>> => {
        setIsSaving(true);
        setErrors([]);
        try {
            const result = await voicifyApi.createMedicalIntegrationConfig(model);
            if (result.resultType === "Ok" && result.data) {
                setMedicalConfigs(prev => [...prev, result.data]);
            } else {
                setErrors(result.errors || ['Failed to create configuration']);
            }
            return result;
        } catch (e) {
            const errorMessage = e?.toString() || 'Unknown error';
            setErrors([errorMessage]);
            return {
                resultType: "InternalError",
                errors: [errorMessage],
                data: null
            } as IResult<MedicalIntegrationConfigModel>;
        } finally {
            setIsSaving(false);
        }
    };

    const updateConfig = async (configId: string, model: MedicalIntegrationConfigModel): Promise<IResult<MedicalIntegrationConfigModel>> => {
        setIsSaving(true);
        setErrors([]);
        try {
            const result = await voicifyApi.updateMedicalIntegrationConfig(configId, model);
            if (result.resultType === "Ok") {
                // Use result.data if available, otherwise use the model we sent with the configId
                const updatedConfig = result.data || { ...model, id: configId };
                const updatedConfigs = medicalConfigs.map(config =>
                    config.id === configId ? updatedConfig : config
                );
                setMedicalConfigs(updatedConfigs);
                setSelectedConfig(updatedConfig);
            } else {
                setErrors(result.errors || ['Failed to update configuration']);
            }
            return result;
        } catch (e) {
            const errorMessage = e?.toString() || 'Unknown error';
            setErrors([errorMessage]);
            return {
                resultType: "InternalError",
                errors: [errorMessage],
                data: null
            } as IResult<MedicalIntegrationConfigModel>;
        } finally {
            setIsSaving(false);
        }
    };

    const selectConfig = (config: MedicalIntegrationConfigModel | null) => {
        setSelectedConfig(config);
    };

    const clearErrors = () => {
        setErrors([]);
    };

    const clearConfigs = () => {
        setMedicalConfigs([]);
        setSelectedConfig(null);
    };

    return {
        medicalConfigs,
        setMedicalConfigs,
        selectedConfig,
        isLoading,
        isSaving,
        errors,
        loadConfigsByOrganizationId,
        createConfig,
        updateConfig,
        selectConfig,
        clearErrors,
        clearConfigs
    };
}

const MedicalIntegrationConfigContainer = createContainer(useMedicalIntegrationConfigContainer);

export default MedicalIntegrationConfigContainer;
